import { faExchangeAlt } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Caption } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary, tradeLabels } from '@sorare/core/src/lib/glossary';

import avatar from './assets/avatar.png';
import baseballCard from './assets/baseball_card.png';
import cash from './assets/cash.png';
import footballCard from './assets/football_card.png';
import nbaCard from './assets/nba_card.png';

const Root = styled(Horizontal)`
  flex-grow: 1;
  width: 100%;
`;
const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  max-width: 400px;
`;
const Metas = styled(Horizontal)`
  align-items: flex-start;
`;
const Avatar = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
`;

const Header = styled(Horizontal)`
  background-color: var(--c-neutral-200);
  border-top-left-radius: var(--intermediate-unit);
  border-top-right-radius: var(--intermediate-unit);
  justify-content: space-between;
  padding: var(--half-unit) var(--unit);
  border-bottom: solid 1px var(--c-neutral-300);
`;

const Relative = styled.div`
  isolation: isolate;
  position: relative;
`;

const Offer = styled.div`
  display: grid;
  grid-template-columns: 1fr var(--quadruple-unit) 1fr;
  gap: var(--unit);
  align-items: flex-end;
  background-color: var(--c-neutral-200);
  padding: var(--unit) var(--unit) 0;
  border-bottom-left-radius: var(--intermediate-unit);
  border-bottom-right-radius: var(--intermediate-unit);
  overflow: hidden;
`;

const OfferSide = styled(Vertical)`
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const Card = styled.img`
  width: 100%;
  max-width: 100px;
  height: auto;
`;

const Label = styled(Caption)`
  color: var(--c-neutral-600);
  text-align: center;
  display: inline-block;
  border-radius: 2px;
  background-color: var(--c-neutral-300);
  padding-inline: var(--half-unit);
`;

const Cursor = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.5124 8.42397L9.70771 6.18448C9.59707 5.87655 9.37514 5.63836 9.10339 5.49932C8.82869 5.35877 8.50374 5.32013 8.19314 5.41043C7.87598 5.50263 7.62818 5.71329 7.48202 5.9792C7.33537 6.24602 7.29096 6.56889 7.38281 6.88483L9.00707 12.4717L8.91835 12.3963C8.55253 12.1283 8.10874 11.9913 7.66239 11.9869C7.22 11.9825 6.7764 12.1085 6.30393 12.435L6.29589 12.4271C6.14477 12.5701 6.05633 12.7668 6.04108 12.9716C6.02604 13.1737 6.08256 13.381 6.20918 13.547L7.84696 15.6935C8.13917 16.0765 8.74256 16.5628 9.18461 16.7667C9.19321 16.7706 11.3205 17.5879 11.4724 18.1106L11.7979 19.2301L17.8908 17.4588L17.5653 16.3392C17.4085 15.7999 18.0113 13.8024 18.0138 13.7932C18.1363 13.3166 18.1283 12.542 17.99 12.0665L17.0956 8.98993C16.9576 8.5449 16.6464 8.19528 16.251 7.99277C15.8653 7.79524 15.3995 7.73812 14.9413 7.8713C14.6964 7.9425 14.4965 8.0909 14.3611 8.27911C14.3116 8.24655 14.2604 8.21651 14.2076 8.18913C13.8232 7.98965 13.3556 7.93152 12.8958 8.0652C12.6509 8.1364 12.451 8.28479 12.3156 8.47301C12.2661 8.44044 12.2149 8.41041 12.1621 8.38302C11.7777 8.18354 11.3101 8.12541 10.8503 8.25909C10.725 8.29551 10.6115 8.35214 10.5124 8.42397Z"
      fill="#101010"
      stroke="white"
    />
  </svg>
);

const StyledCursor = styled(Cursor)`
  position: absolute;
  right: 0;
  top: 50%;
`;

const Icon = styled.div`
  padding-bottom: var(--triple-unit);
`;

const titleBySport = {
  [Sport.FOOTBALL]: defineMessage({
    id: 'forYou.firstTime.mobileNotification.title',
    defaultMessage: 'Kiks027 sent you an offer',
  }),
  [Sport.BASEBALL]: defineMessage({
    id: 'forYou.baseball.firstTime.mobileNotification.title',
    defaultMessage: 'Shohei017 sent you an offer',
  }),
  [Sport.NBA]: defineMessage({
    id: 'forYou.nba.firstTime.mobileNotification.title',
    defaultMessage: 'Wemby1 sent you an offer',
  }),
};

const cardBySport = {
  [Sport.FOOTBALL]: footballCard,
  [Sport.BASEBALL]: baseballCard,
  [Sport.NBA]: nbaCard,
};

export const MobileNotification = () => {
  const { sport = Sport.FOOTBALL } = useSportContext();

  return (
    <Root center>
      <Wrapper>
        <Header>
          <Metas>
            <Avatar src={avatar} alt="avatar" />
            <Ellipsis>
              <Caption color="var(--c-neutral-600)">
                <FormattedMessage {...titleBySport[sport]} />
              </Caption>
            </Ellipsis>
          </Metas>
          <Relative>
            <Chip
              custom={{
                color: 'var(--c-green-600)',
                background: 'var(--c-green-300)',
              }}
              size="smaller"
              label={<FormattedMessage {...glossary.accept} />}
            />
            <StyledCursor />
          </Relative>
        </Header>
        <Offer>
          <OfferSide>
            <Label>
              <FormattedMessage {...tradeLabels.youSend} />
            </Label>
            <Card src={cardBySport[sport]} alt="card" />
          </OfferSide>
          <Icon>
            <FontAwesomeIcon icon={faExchangeAlt} size="2x" />
          </Icon>
          <OfferSide>
            <Label>
              <FormattedMessage {...tradeLabels.youReceive} />
            </Label>
            <Card src={cash} alt="card" />
          </OfferSide>
        </Offer>
      </Wrapper>
    </Root>
  );
};
